/**
 * A sub-patch is an encapsulated network of zobjects
 * and zmessages, and has a dynamic set of inlets and outlets,
 * letting you connect the sub-patch to the rest of your patch.
 */

import {inletCreated, outletCreated} from './subpatch/operations.js';
import {zobjectCreated} from './subpatch/operations.js';
import {zmessageCreated} from './subpatch/operations.js';
import {
    setLoadingSequence,
    setViewableSubPatches} from 'actions/index.js';
import Propagator from './subpatch/Propagator.js';
import Instancing from './Instancing.js';
import {
    connectPresetToRemainingGraph,
    connectionJson} from './utils/connectionJson.js';
import {uuid} from 'uuidv4';
import StepData from "pattern/StepData.js";
import ZObject from './ZObject.js';
import ZObjects from "./ZObjects.js";
import Dispatchable from "utils/Dispatchable.js";
import NodeMessaging from "messaging/NodeMessaging.js";

class SubPatch extends Propagator(Instancing(NodeMessaging(ZObjects(Dispatchable(class {}))))) {
    
    constructor(subSequence, sequencerEngine, zObject, name) {
        super();
        this.id = uuid();
        this.subSequence = subSequence;
        this.sequencerEngine = sequencerEngine;
        this.sequencerEngine.idToObject[this.id] = this;
        this.zObject = zObject;
        this.name = name;
        this.trackData = new StepData();
        this.trackData.loadDefaults();

        this.inletObjects = [];
        this.outletObjects = [];

        this.newInlet(1);
        this.newOutlet(1);

        this.zObject.newAction("Create Instance", () => this.newInstance());

        this.outletInstanceMapping = {};
        this.inletInstanceMapping = {};

        this.isInitialLoad = true;
        this.initPattr();
    }

    getRootSubSequence() {
        if (this.subSequence.getRootSubSequence) {
            return this.subSequence.getRootSubSequence();
        }
        return this.subSequence;
    }

    getJson(isPreset, isRoot) {
        let inbound, outbound, objectDependencies;
        if (isPreset) {
            let _inbound = connectionJson(this.getInboundNodes());
            let _outbound = connectionJson(this.getOutboundNodes());
            let effects = Array.from(new Set([... _inbound.effects, ..._outbound.effects]));
            let midiEffects = Array.from(new Set([... _inbound.midiEffects, ..._outbound.midiEffects]));
            inbound = _inbound.json;
            outbound =  _outbound.json;

            objectDependencies = {
                effects: effects.map(x => x.getJson()),
                midiEffects: midiEffects.map(x => x.getJson()),
            };
        }
        return {
            id: this.id,
            ... this.getZObjectsJson(isPreset),
            numInlets: this.inletObjects.length,
            inbound,
            outbound,
            objectDependencies,
            fillColor: this.fillColor,
            numOutlets: this.outletObjects.length,
            isCustom: this.isCustom,
            width: this.width,
            height: this.height,
            inletObjects: this.inletObjects.map(x => x.getJson()),
            outletObjects: this.outletObjects.map(x => x.getJson()),
            instanceInlets: this.instanceInlets.filter(x => x).map(x => x.id),
            instances: this.instances.filter(x => x).map(x => x.id),
            outletInstanceMapping: this.outletInstanceMapping,
            inletInstanceMapping: this.inletInstanceMapping,
            pattr: this.pattr.getJson()
        };
    }

    getParameters() {
        return this.zObjects.flatMap(
            x => x.getAllParameters());
    }

    fromJson(json, isCopy=false, isInstance, isRoot, hide) {
        // FOR SUBPATCH INSTANCES WE NEED TO NOT COPY ID
        this.zMessages.forEach(
            p => {
                if (isInstance && (p.isInlet  || p.isOutlet)) {
                } else {
                    this.deleteNode(p, true);
                }
            });
        this.zObjects.forEach(
            p => this.deleteNode(p, true));

        if (!isCopy) {
            this.id = json.id;
            this.sequencerEngine.idToObject[this.id] = this;
        }

        this.fillColor = json.fillColor;
        this.isCustom = json.isCustom;
        this.width = json.width;
        this.height = json.height;

        if (this.isCustom) {
            this.zObject.width = this.width;
            this.zObject.height = this.height;
        }
        
        let idToId = {};
        let {zObjects, zMessages} = json;
        for (let obj of zObjects) {
            let zobject = this.createZObject();
            let tmp = zobject.fromJson(obj, isCopy);
            idToId = {
                ... idToId,
                ... tmp
            };
            idToId[obj.id] = zobject.id;

            if (zobject.effect && obj.effect) {
                idToId[obj.effect.id] = zobject.effect.id;
            }
        }

        this.zMessages = this.zMessages.filter(
            x => isInstance || (!x.isOutlet && !x.isInlet));
        
        for (let obj of zMessages) {
            if (obj.outletNumber !== undefined)  {
                let type = 'Output ' + obj.outletNumber;
                if (this.outlets[type] && isInstance) {
                    if (!this.outletInstanceMapping[obj.id]) {
                        let _id = uuid();
                        this.outletInstanceMapping[obj.id] = _id;

                        let connections = [];
                        if (this.outlets[type]) {
                            // outlet already exists so we delete it?
                            connections = this.outlets["Output " + obj.outletNumber].connections;
                            this.deleteOutlet('Output ' + obj.outletNumber);
                            this.outletObjects.splice(obj.outletNumber - 1, 1);
                        }

                        this.newOutlet(obj.outletNumber, _id);
                        for (let {destNode, sourceType, destType} of connections) {
                            destNode.connectInlet(destType, this, sourceType);
                        }
                    }  else {
                    }
                    idToId[obj.id] = this.outletInstanceMapping[obj.id];
                } else {
                    if (this.outlets[type]) {
                        // outlet already exists so we delete it?
                        this.deleteOutlet('Output ' + obj.outletNumber);
                        this.outletObjects.splice(obj.outletNumber - 1, 1);
                    }
                    let _id = isCopy ? uuid() : obj.id;
                    this.newOutlet(obj.outletNumber, _id);
                    idToId[obj.id] = _id;
                }
            } else if (obj.inletNumber !== undefined) { 
                if (this.inlets["Input " + obj.inletNumber] && isInstance) {
                    if (!this.inletInstanceMapping[obj.id]) {
                        let _id = uuid();
                        this.inletInstanceMapping[obj.id] = _id;

                        if (this.inlets["Input " + obj.inletNumber]) {
                            this.deleteInlet('Input ' + obj.inletNunmber);
                            this.inletObjects.splice(obj.inletNumber - 1, 1);
                        }
                        this.newInlet(obj.inletNumber, _id);
                    } else {
                    }
                    idToId[obj.id] = this.inletInstanceMapping[obj.id];
                } else {
                    if (this.inlets['Input ' + obj.inletNumber]) {
                        this.deleteInlet('Input ' + obj.inletNunmber);
                        this.inletObjects.splice(obj.inletNumber - 1, 1);
                    }
                    let _id = !isCopy ? obj.id : uuid();
                    this.newInlet(obj.inletNumber, _id);
                    idToId[obj.id] = _id;
                    this.inletInstanceMapping[obj.id] = _id;
                }
            } else {
                let zmsg = this.createZMessage();
                zmsg.fromJson(obj, isCopy);
                idToId[obj.id] = zmsg.id;
            }
        }

        if (json.inletObjects) {
            for (let i=0; i < json.inletObjects.length; i++) {
                if (this.inletObjects[i]) {
                    this.inletObjects[i]._text = json.inletObjects[i].text;
                }
            }
            for (let i=0; i < json.outletObjects.length; i++) {
                if (this.outletObjects[i]) {
                    this.outletObjects[i]._text = json.outletObjects[i].text;
                }
            }
        }

        if (json.instanceInlets && !isInstance) {
            for (let instanceInlet of json.instanceInlets) {
                let obj = this.inletObjects.find(x => x.id === instanceInlet || x.id  === idToId[instanceInlet]);
                this.instanceInlets.push(obj);
            }
        }

        if (json.instances && !isInstance) {
            this.instanceIds = json.instances;
        }

        if (!isInstance && isRoot) {
            if (json.outbound || json.inbound) {
                this.sequencerEngine.dispatch(setLoadingSequence(this.sequencerEngine.sequenceId, false));
                connectPresetToRemainingGraph(this, json.inbound, json.outbound, json.objectDependencies, hide);
                this.sequencerEngine.dispatch(setLoadingSequence(this.sequencerEngine.sequenceId, true));
            }
        }

        this.updateMessagingRedux();
        this.isInitialLoad = false;
        this.pattr.fromJson(json.pattr);

        return idToId;
    }

    deencapsulate() {
        this.zMessages.forEach(
            x => {
                x.isInlet = false;
                x.isOutlet = false;
            });
        this.subSequence.zMessages = [
            ... this.subSequence.zMessages,
            ... this.zMessages
        ];

        this.subSequence.zObjects = [
            ... this.subSequence.zObjects,
            ... this.zObjects
        ];

        this.subSequence.zObjects = this.subSequence.zObjects.filter(
            x => x !== this.zObject);

        this.subSequence.updateZObjectRedux();
        this.subSequence.updateZMessageRedux();

        for (let zMessage of this.zMessages) {
            zMessage.clearMessagingRedux();
            zMessage.subSequence = this.subSequence;
            zMessage.updateMessagingRedux();
        }

        for (let zObject of this.zObjects) {
            if (zObject.effect) {
                if (zObject.effect.clearMessagingRedux) {
                zObject.effect.clearMessagingRedux();
                    
                }
                zObject.effect.subSequence = this.subSequence;
                if (zObject.effect.updateMessagingRedux) {
                    zObject.effect.updateMessagingRedux();
                }
            }
            zObject.clearMessagingRedux();
            zObject.subSequence = this.subSequence;
            zObject.updateMessagingRedux();
        }

        this.zMessages = [];
        this.zObjects = [];
        
        this.updateZObjectRedux();
        this.updateZMessageRedux();

        this.dispatch(
            setViewableSubPatches([]));
        
    }

    newZObject() {
        return new ZObject(this);
    }

    newInlet(number=this.inletObjects.length+1, id, x, y, propagate=true) {
        let type = "Input " + number;
        x = x || 50 + 100*(number-1);
        y = y || 50;
        let zMessage = this.createZMessage(x,y,undefined,undefined,id);
        zMessage.isInlet = true;
        zMessage.inletNumber = number;
        zMessage.deleteOutlet("Message Send");
        this.inletObjects.push(zMessage);

        // create a new inlet that pipes any messages it receeivse through the
        // zMessage
        super.newInlet(type, (msg) =>
            this.onInletMessage(zMessage, msg));

        if (propagate) {
            this.propagate(
                inletCreated(zMessage, false));
        }
        return zMessage;
    }

    newOutlet(number=this.outletObjects.length+1, id, x, y) {
        let type = "Output " + number;
        super.newOutlet(type);

        // create a new output zMessage node that has a callback
        // where: on all new messages, route the message out
        // this SubPath's outlet

        x = x || 50 + 100*(number-1);
        y = y || 350;

        let zMessage = this.createZMessage(
            x, y, undefined,
            (msg, ...args) => this.sendMessage(type, msg, ...args),
            id);
        zMessage.linkedOutlet = this.outlets[type];
        zMessage.outletNumber = number;
        zMessage.isOutlet = true;
        this.outletObjects.push(zMessage);

        this.propagate(
            outletCreated(zMessage));
        return zMessage;
    }

    evaluateAsFunction(arg, ...args) {
        let inlet = this.inletObjects[0];
        if (!inlet) {
            return;
        }

        let outlet = this.outletObjects[0];
        let o = first(inlet.outlets);
        while (o) {
            let connection = o.connections[0];
            if (connection) {
                let nextNode = connection.destNode;
                if (nextNode.isOutlet) {
                    break;
                }
                let _arg = arg;
                arg = nextNode.function([arg]);
                if (Array.isArray(arg)) {
                    arg = arg[0];
                } 
                o = first(nextNode.outlets);
            } else {
                break;
            }
        }

        return arg;
    }


    dispose() {
        for (let instance of this.instances) {
            instance.zObject.delete();
        }
    }

    isDeletable() {
        return true;
    }

    createZObject(...args) {
        let zobject = super.createZObject(...args);

        this.propagate(zobjectCreated(zobject));
        return zobject;
    }

    createZMessage(...args) {
        let zmessage = super.createZMessage(...args);

        this.propagate(zmessageCreated(zmessage));
        return zmessage;
    }

}

const first = (outlets) => {
    return outlets[Object.keys(outlets)[0]];
};


export default SubPatch;
